import React, { useContext } from 'react';
import { LangContext } from '../../contexts/langContext';
import astro_placements from '../../static/astro_placements.json';
import { AstroBg, AstroSign } from '../UiKit/AstroSign';
import { AstroIcon } from '../UiKit/AstroIcon';
import { TooltipH } from '../UiKit/TooltipH';
import { Txt } from '../UiKit/Txt';
import { formatDegrees } from './utilsAstro';
import '../../components/css/astro.css';

export const ASTRO_SUMMARY_SECTIONS = ['sun', 'asc', 'mc', 'north_node', 'south_node'];

const getSignLabelFromDegree = (decimal) => {
  const signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
  if (typeof decimal !== 'number' || isNaN(decimal)) return '';
  const norm = ((decimal % 360) + 360) % 360;
  const index = Math.floor(norm / 30) % 12;
  return signs[index];
};

const formatDecimalArc30 = (decimal) => {
  if (typeof decimal !== 'number' || isNaN(decimal)) return '';
  const norm = ((decimal % 360) + 360) % 360;
  const degWithin = norm % 30;
  const degInt = Math.floor(degWithin);
  const minutes = Math.round((degWithin - degInt) * 60);
  return `${degInt}° ${String(minutes).padStart(2, '0')}'`;
};

const wrapItem = (itemClassName, key, children) => {
  if (!itemClassName) return children;
  return <div key={key} className={itemClassName}>{children}</div>;
};

const PlacementCell = ({
  sign,
  degrees,
  icon,
  tooltipTitle,
  tooltipDesc,
  title,
  description,
}) => (
  <div className="astro-summary-cell-container">
    <div className="astro-summary-cell _fv _fgap0">
      <AstroBg sign={sign} />
      <div className="_fv _fm _tc _fgap0">
        <div className="astro-header">
          <TooltipH title={<div className="_fv _fgap02 _t08">
            <b>{tooltipTitle}</b>
            <Txt>{tooltipDesc}</Txt>
          </div>}>
            <span className="astro-summary-cell-icon">
              {icon}
            </span>
          </TooltipH>
        </div>
        <AstroSign sign={sign} />
        <SignLabel sign={sign} />
        <span className="astro_degrees">{degrees || ''}</span>
      </div>
    </div>
    <div className="astro-summary-cell-description">
      <b>{title}</b>
      <Txt>{description}</Txt>
    </div>
  </div>
);

const SignLabel = ({ sign }) => {
  const context = useContext(LangContext);
  return <strong className="astro_sign">{context.$ui(sign) || 'N/A'}</strong>;
};

/** Summary cells: Sun, Asc, MC, Nodes — shared by AstroInfo and PentaAstro */
export function AstroSummary({ horoscope, itemClassName, only }) {
  const context = { ...useContext(LangContext) };
  const $ui = context.$ui;
  const $placement = (point, sign) => astro_placements[point]?.[sign]?.[context.language] || astro_placements[point]?.[sign]?.en || '';
  const $placementTitle = (titleKey, sign) => sign ? `${$ui(titleKey)} ${$ui(`sign_in_${sign}`)}` : '';

  if (!horoscope) return null;

  const celestialPointsAll = horoscope?.CelestialPoints?.all || [];
  const trueNorthNode = celestialPointsAll.find(p => p?.key === 'northnode' || (p?.label || '').toLowerCase() === 'north node');
  const trueSouthNode = celestialPointsAll.find(p => p?.key === 'southnode' || (p?.label || '').toLowerCase() === 'south node');

  const southFromTrue = (!trueSouthNode && trueNorthNode) ? (() => {
    const deg = (trueNorthNode?.ChartPosition?.Ecliptic?.DecimalDegrees + 180) % 360;
    return {
      Sign: { label: getSignLabelFromDegree(deg) },
      ChartPosition: { Ecliptic: { DecimalDegrees: deg, ArcDegreesFormatted30: formatDecimalArc30(deg) } },
    };
  })() : null;

  const southNode = trueSouthNode || southFromTrue;

  const sections = {
    sun: (
      <PlacementCell
        sign={horoscope.SunSign?.label}
        degrees={formatDegrees(horoscope.CelestialBodies?.all?.find(p => p.label === 'Sun')?.ChartPosition?.Ecliptic?.ArcDegreesFormatted30) || ''}
        icon={<AstroIcon name="Sun" size={24} color="#A8A8A8" />}
        tooltipTitle={$ui('astro_sun_sign')}
        tooltipDesc={$ui('astro_sun_sign_description')}
        title={$placementTitle('planet_Sun', horoscope.SunSign?.label)}
        description={$placement('Sun', horoscope.SunSign?.label)}
      />
    ),
    asc: (
      <PlacementCell
        sign={horoscope.Ascendant?.Sign?.label}
        degrees={formatDegrees(horoscope.Ascendant?.ChartPosition?.Ecliptic?.ArcDegreesFormatted30) || ''}
        icon={<AstroIcon name="asc" size={20} color="#A8A8A8" />}
        tooltipTitle={$ui('astro_ascendant')}
        tooltipDesc={$ui('astro_ascendant_description')}
        title={$placementTitle('astro_ascendant', horoscope.Ascendant?.Sign?.label)}
        description={$placement('Ascendant', horoscope.Ascendant?.Sign?.label)}
      />
    ),
    mc: (
      <PlacementCell
        sign={horoscope.Midheaven?.Sign?.label}
        degrees={formatDegrees(horoscope.Midheaven?.ChartPosition?.Ecliptic?.ArcDegreesFormatted30) || ''}
        icon={<AstroIcon name="mc" size={20} color="#A8A8A8" />}
        tooltipTitle={$ui('astro_midheaven')}
        tooltipDesc={$ui('astro_midheaven_description')}
        title={$placementTitle('astro_midheaven', horoscope.Midheaven?.Sign?.label)}
        description={$placement('Midheaven', horoscope.Midheaven?.Sign?.label)}
      />
    ),
    north_node: (
      <PlacementCell
        sign={trueNorthNode?.Sign?.label}
        degrees={formatDegrees(trueNorthNode?.ChartPosition?.Ecliptic?.ArcDegreesFormatted30) || ''}
        icon={<AstroIcon name="North_Node" size={20} color="#A8A8A8" />}
        tooltipTitle={$ui('planet_North_Node')}
        tooltipDesc={$ui('astro_north_node_description')}
        title={$placementTitle('planet_North_Node', trueNorthNode?.Sign?.label)}
        description={$placement('North_Node', trueNorthNode?.Sign?.label)}
      />
    ),
    south_node: (
      <PlacementCell
        sign={southNode?.Sign?.label}
        degrees={formatDegrees(southNode?.ChartPosition?.Ecliptic?.ArcDegreesFormatted30) || ''}
        icon={<AstroIcon name="South_Node" size={20} color="#A8A8A8" />}
        tooltipTitle={$ui('planet_South_Node')}
        tooltipDesc={$ui('astro_south_node_description')}
        title={$placementTitle('planet_South_Node', southNode?.Sign?.label)}
        description={$placement('South_Node', southNode?.Sign?.label)}
      />
    ),
  };

  const keys = only ? [only] : ASTRO_SUMMARY_SECTIONS;
  const cells = keys.map((key) => wrapItem(itemClassName, key, sections[key]));

  if (only) {
    return cells[0] || null;
  }

  return (
    <div className="x_astrology_summary" style={{
      position: 'relative',
      borderRadius: '0.4em',
    }}>
      {cells}
    </div>
  );
}
